import { GlobeVisualization } from './components/globe.js';
import { startScan, pollProgress, getScanResult, getRecentScans, getStats, getGlobeData, fetchLogs, fetchScanLogs, fetchComparison } from './utils/api.js';
import { exportScanAsJson, exportScanAsCsv, exportScanAsHtmlReport, exportHistoryAsCsv } from './utils/exportUtils.js';

// ============================================================
// INITIALIZATION
// ============================================================
let globe;
let currentScanId = null;
let pollInterval = null;
let currentScanResult = null; // Holds last completed scan for export

document.addEventListener('DOMContentLoaded', () => {
  initClock();
  initGlobe();
  initGlobeControls();
  initScanner();
  initTerminal();
  initHistory();
  initLogs();
  initComparison();
  initAbout();
  loadInitialData();
});

// ============================================================
// ABOUT MODAL
// ============================================================
function initAbout() {
  const aboutBtn = document.getElementById('about-btn');
  const aboutModal = document.getElementById('about-modal');
  const closeAbout = document.getElementById('close-about');

  aboutBtn.addEventListener('click', () => {
    aboutModal.classList.remove('hidden');
  });

  closeAbout.addEventListener('click', () => {
    aboutModal.classList.add('hidden');
  });

  // Close on overlay click
  aboutModal.addEventListener('click', (e) => {
    if (e.target === aboutModal) {
      aboutModal.classList.add('hidden');
    }
  });

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !aboutModal.classList.contains('hidden')) {
      aboutModal.classList.add('hidden');
    }
  });
}


// ============================================================
// CLOCK
// ============================================================
function initClock() {
  const clockEl = document.getElementById('hud-clock');
  function tick() {
    const now = new Date();
    clockEl.textContent = now.toTimeString().substring(0, 8);
  }
  tick();
  setInterval(tick, 1000);
}

// ============================================================
// GLOBE
// ============================================================
function initGlobe() {
  const canvas = document.getElementById('globe-canvas');
  globe = new GlobeVisualization(canvas);
}

function initGlobeControls() {
  const bordersToggle = document.getElementById('toggle-borders');
  bordersToggle.addEventListener('change', (e) => {
    globe.setBordersVisible(e.target.checked);
    showToast(e.target.checked ? 'Country borders enabled' : 'Country borders disabled', 'info');
  });
}

// ============================================================
// SCANNER
// ============================================================
function initScanner() {
  const input = document.getElementById('scan-input');
  const btn = document.getElementById('scan-btn');

  btn.addEventListener('click', () => runScan());
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') runScan();
  });

  // Abort button
  document.getElementById('abort-btn').addEventListener('click', () => abortScan());
}

function abortScan() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
  currentScanId = null;

  const btn = document.getElementById('scan-btn');
  const progressEl = document.getElementById('scan-progress');
  const statusEl = document.getElementById('scanner-status');

  btn.disabled = false;
  btn.classList.remove('scanning');
  statusEl.textContent = 'READY';
  statusEl.classList.remove('scanning');
  progressEl.classList.add('hidden');

  terminalLog('', 'system');
  terminalLog('✕ SCAN ABORTED by user', 'error');
  terminalLog('  Scanner reset. Ready for new target.', 'info');
  showToast('Scan aborted', 'warning');
}

async function runScan() {
  const input = document.getElementById('scan-input');
  const btn = document.getElementById('scan-btn');
  const progressEl = document.getElementById('scan-progress');
  const statusEl = document.getElementById('scanner-status');
  const url = input.value.trim();

  if (!url) {
    showToast('Enter a target URL', 'warning');
    return;
  }

  // Disable UI
  btn.disabled = true;
  btn.classList.add('scanning');
  statusEl.textContent = 'SCANNING';
  statusEl.classList.add('scanning');
  progressEl.classList.remove('hidden');

  terminalLog('', 'system');
  terminalLog(`▸ INITIATING SCAN: ${url}`, 'success');
  terminalLog(`  Target acquired. Running 6 detection vectors...`, 'info');

  try {
    const { scanId, domain } = await startScan(url);
    currentScanId = scanId;
    terminalLog(`  Scan ID: ${scanId.substring(0, 8)}...`, 'system');

    // Start polling
    pollInterval = setInterval(async () => {
      try {
        const progress = await pollProgress(scanId);
        updateProgress(progress.progress, progress.currentStep);
        terminalLog(`  [${progress.progress}%] ${progress.currentStep}`, 'scanner');

        if (progress.status === 'complete') {
          clearInterval(pollInterval);
          pollInterval = null;
          const result = progress.result || await getScanResult(scanId);
          onScanComplete(result);
        } else if (progress.status === 'error') {
          clearInterval(pollInterval);
          pollInterval = null;
          onScanError(progress.error);
        }
      } catch (e) {
        // Keep polling
      }
    }, 1500);

  } catch (e) {
    onScanError(e.message);
  }
}

function updateProgress(pct, label) {
  document.getElementById('progress-fill').style.width = `${pct}%`;
  document.getElementById('progress-label').textContent = label;
  document.getElementById('progress-percent').textContent = `${pct}%`;
}

function onScanComplete(result) {
  const btn = document.getElementById('scan-btn');
  const progressEl = document.getElementById('scan-progress');
  const statusEl = document.getElementById('scanner-status');

  btn.disabled = false;
  btn.classList.remove('scanning');
  statusEl.textContent = 'READY';
  statusEl.classList.remove('scanning');
  progressEl.classList.add('hidden');

  terminalLog('', 'system');
  terminalLog(`✓ SCAN COMPLETE — ${result.domain}`, 'success');
  terminalLog(`  Risk Score: ${result.overallRisk}/100 | Threat Level: ${result.threatLevel}`, 
    result.threatLevel === 'CRITICAL' ? 'error' : result.threatLevel === 'HIGH' ? 'warning' : 'info');
  terminalLog(`  Duration: ${(result.scanDuration / 1000).toFixed(1)}s | Location: ${result.country || 'Unknown'}`, 'system');

  if (result.details?.risk?.detectedTools?.length > 0) {
    // FIX: extract .label from each tool object to avoid [object Object]
    const toolLabels = result.details.risk.detectedTools.map(t => t.label || String(t)).join(', ');
    terminalLog(`  Detected: ${toolLabels}`, 'warning');
  }

  // Store for export
  currentScanResult = result;

  // Show result panel
  showResultPanel(result);

  // Update globe
  if (result.latitude && result.longitude) {
    globe.addPin(result.latitude, result.longitude, result.threatLevel, result.domain);
    globe.removeScanBeams();
  }

  // Update dashboard & history
  refreshDashboard();
  refreshHistory();

  // Refresh logs panel
  refreshLogs();

  // Update DEFCON
  updateDefcon(result.threatLevel);

  showToast(`Scan complete: ${result.domain} — ${result.threatLevel}`,
    result.overallRisk >= 50 ? 'error' : 'success');
}

function onScanError(message) {
  const btn = document.getElementById('scan-btn');
  const progressEl = document.getElementById('scan-progress');
  const statusEl = document.getElementById('scanner-status');

  btn.disabled = false;
  btn.classList.remove('scanning');
  statusEl.textContent = 'READY';
  statusEl.classList.remove('scanning');
  progressEl.classList.add('hidden');

  terminalLog(`✗ SCAN FAILED: ${message}`, 'error');
  terminalLog(`  Check LOGS & DEBUG panel for details`, 'warning');
  showToast(`Scan error: ${message}`, 'error');

  // Auto-refresh logs to show error details
  refreshLogs();
}

// ============================================================
// RESULT PANEL
// ============================================================
function showResultPanel(result) {
  const panel = document.getElementById('result-panel');
  const content = document.getElementById('result-content');
  panel.classList.remove('hidden');

  // Store for export access
  currentScanResult = result;

  const level = result.threatLevel.toLowerCase();
  const riskColor = getRiskColor(result.threatLevel);

  // Build a map of scanner data for quick lookup in drill-down
  const scannerMap = {};
  if (result.details?.scanners) {
    for (const s of result.details.scanners) {
      scannerMap[s.code] = s;
    }
  }

  // --- Scanners HTML (collapsible by scanner code) ---
  let scannersHtml = '';
  if (result.details?.scanners) {
    scannersHtml = result.details.scanners.map(s => {
      const findingsHtml = s.findings.length > 0
        ? s.findings.map(f => `
            <div class="finding">
              <span class="finding-severity sev-${f.severity}">${f.severity}</span>
              <span>${escapeHtml(f.message)}</span>
            </div>
          `).join('')
        : '<div class="finding" style="color:var(--text-dim)">No findings detected for this scanner.</div>';

      return `
        <div class="scanner-result" onclick="this.classList.toggle('open')">
          <div class="scanner-result-header">
            <span class="scanner-code">${s.code}</span>
            <span class="scanner-name">${s.name}</span>
            <span class="scanner-score" style="color:${getRiskColor(getLevel(s.score))}">${s.score}/100</span>
          </div>
          <div class="scanner-result-body">${findingsHtml}</div>
        </div>
      `;
    }).join('');
  }

  // --- Explainability: Score Formula with Audit Drill-Down ---
  const risk = result.details?.risk;
  let formulaHtml = '';
  if (risk?.scoreFormula?.length > 0) {
    const rows = risk.scoreFormula.map(f => {
      const pct = Math.round(f.weight * 100);
      const barW = Math.min(f.score, 100);
      const color = getRiskColor(getLevel(f.score));
      const scanner = scannerMap[f.code];

      // Build audit drill-down body
      let auditFindingsHtml = '';
      if (scanner?.findings?.length > 0) {
        auditFindingsHtml = scanner.findings.map(fi => `
          <div class="audit-finding-row">
            <span class="finding-severity sev-${fi.severity}">${fi.severity}</span>
            <span class="audit-finding-msg">${escapeHtml(fi.message)}</span>
          </div>
        `).join('');
      } else {
        auditFindingsHtml = '<div class="audit-no-findings">✓ No findings — clean result for this scanner</div>';
      }

      const rawJson = scanner ? JSON.stringify({ code: scanner.code, name: scanner.name, score: scanner.score, findings: scanner.findings }, null, 2) : '{}';

      return `
        <div class="expl-row clickable" data-code="${f.code}" onclick="this.nextElementSibling.classList.toggle('open')">
          <span class="expl-code">${f.code}</span>
          <span class="expl-name">${f.name}</span>
          <div class="expl-bar-wrap">
            <div class="expl-bar" style="width:${barW}%;background:${color}"></div>
          </div>
          <span class="expl-score" style="color:${color}">${f.score}</span>
          <span class="expl-weight">×${pct}%</span>
          <span class="expl-contrib">= ${f.contribution}</span>
        </div>
        <div class="expl-audit-body">
          <div class="audit-header">
            <span class="audit-scanner-label">${f.code} — ${f.name}</span>
            <span class="audit-formula">Score: <strong style="color:${color}">${f.score}/100</strong> × weight ${pct}% = <strong style="color:var(--purple-dim)">${f.contribution}</strong> pts</span>
          </div>
          <div class="audit-findings-list">${auditFindingsHtml}</div>
          <div class="audit-raw-section">
            <button class="audit-raw-toggle" onclick="event.stopPropagation();this.nextElementSibling.classList.toggle('open')">{ } Raw Scanner Data</button>
            <pre class="audit-raw-block">${escapeHtml(rawJson)}</pre>
          </div>
        </div>
      `;
    }).join('');

    const totalW = risk.scoreFormula.reduce((s, f) => s + f.weight, 0);
    const sumContrib = risk.scoreFormula.reduce((s, f) => s + f.contribution, 0);
    formulaHtml = `
      <div class="expl-section">
        <div class="expl-section-title">⚖️ Score Formula Trace <span class="expl-hint">(click any row to audit)</span></div>
        <div class="expl-formula-note">Overall Risk = Σ(scanner_score × weight) ÷ Σ(weights)</div>
        ${rows}
        <div class="expl-total-row">
          <span>Σ weighted scores: <strong>${sumContrib.toFixed(1)}</strong></span>
          <span>÷ Σ weights: <strong>${totalW.toFixed(2)}</strong></span>
          <span>= Risk: <strong style="color:${riskColor}">${result.overallRisk}/100</strong></span>
        </div>
      </div>
    `;
  }

  // --- Explainability: Detected Tools ---
  let toolsHtml = '';
  if (risk?.detectedTools?.length > 0) {
    const toolCards = risk.detectedTools.map(t => {
      const isFP = t.confidence === 'LOW';
      const confColor = t.confidence === 'HIGH' ? '#44dd66' : t.confidence === 'MEDIUM' ? '#ffaa22' : '#666';
      const confIcon = t.confidence === 'HIGH' ? '🟢' : t.confidence === 'MEDIUM' ? '🟡' : '⚫';
      return `
        <div class="expl-tool-card ${isFP ? 'expl-tool-fp' : ''} expl-tool-conf-${(t.confidence||'').toLowerCase()}">
          <div class="expl-tool-header">
            <span class="expl-tool-label">${escapeHtml(t.label)}</span>
            <span class="expl-tool-conf" style="color:${confColor}">${confIcon} ${t.confidence || '?'} confidence</span>
          </div>
          <div class="expl-tool-detail">
            <div class="expl-tool-trigger">
              <span class="expl-key">Triggered by:</span>
              <span class="expl-trigger-text">${escapeHtml(t.triggerReason || '—')}</span>
            </div>
            <div class="expl-tool-meta">
              <span class="expl-key">Scanner:</span> <span class="expl-val">${t.triggerScanner || '—'}</span>
              &nbsp;|&nbsp;
              <span class="expl-key">Score at trigger:</span> <span class="expl-val" style="color:${getRiskColor(getLevel(t.triggerScore || 0))}">${t.triggerScore ?? '—'}/100</span>
            </div>
            ${isFP && t.falsePositiveWarning ? `<div class="expl-fp-warning">⚠️ ${escapeHtml(t.falsePositiveWarning)}</div>` : ''}
          </div>
        </div>
      `;
    }).join('');

    toolsHtml = `
      <div class="expl-section">
        <div class="expl-section-title">🔎 Detected Tools — Reasoning Trace</div>
        ${toolCards}
      </div>
    `;
  }

  const explainHtml = (formulaHtml || toolsHtml) ? `
    <div class="explainability-panel expl-open">
      <div class="expl-header" onclick="this.parentElement.classList.toggle('expl-open')">
        <span>🧠 Explainability &amp; Score Reasoning</span>
        <span class="expl-toggle-icon">▼</span>
      </div>
      <div class="expl-body">
        ${formulaHtml}
        ${toolsHtml}
      </div>
    </div>
  ` : '';

  content.innerHTML = `
    <div class="result-summary ${level}">
      <div class="risk-meter">
        <span class="risk-score ${level}">${result.overallRisk}</span>
        <div class="risk-bar-outer">
          <div class="risk-bar-inner" style="width:${result.overallRisk}%;background:${riskColor}"></div>
        </div>
        <span class="threat-badge threat-${result.threatLevel}">${result.threatLevel}</span>
      </div>
      <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:0.4rem;">
        ${result.domain} • ${result.country || 'Unknown'} • ${(result.scanDuration/1000).toFixed(1)}s
      </div>
    </div>
    ${explainHtml}
    ${scannersHtml}
  `;

  // Close button
  document.getElementById('close-results').onclick = () => panel.classList.add('hidden');

  // Wire export buttons (injected into panel header by index.html)
  const btnJson  = document.getElementById('result-export-json');
  const btnCsv   = document.getElementById('result-export-csv');
  const btnHtml  = document.getElementById('result-export-html');
  if (btnJson)  btnJson.onclick  = () => exportScanAsJson(result);
  if (btnCsv)   btnCsv.onclick   = () => exportScanAsCsv(result);
  if (btnHtml)  btnHtml.onclick  = () => exportScanAsHtmlReport(result);
}


function getLevel(score) {
  if (score >= 75) return 'CRITICAL';
  if (score >= 50) return 'HIGH';
  if (score >= 25) return 'MEDIUM';
  if (score >= 10) return 'LOW';
  return 'CLEAN';
}

function getRiskColor(level) {
  const colors = { CRITICAL: '#ff2255', HIGH: '#ff6644', MEDIUM: '#ffaa22', LOW: '#44dd66', CLEAN: '#22ccff' };
  return colors[level] || '#666';
}

// ============================================================
// TERMINAL
// ============================================================
function initTerminal() {
  document.getElementById('clear-terminal').onclick = () => {
    const output = document.getElementById('terminal-output');
    output.innerHTML = '<div class="term-line system">Terminal cleared.</div><div class="term-cursor">█</div>';
  };
}

function terminalLog(message, type = 'info') {
  const output = document.getElementById('terminal-output');
  const cursor = output.querySelector('.term-cursor');
  const line = document.createElement('div');
  line.className = `term-line ${type}`;
  const ts = new Date().toTimeString().substring(0, 8);
  line.innerHTML = `<span class="timestamp">[${ts}]</span>${escapeHtml(message)}`;
  if (cursor) output.insertBefore(line, cursor);
  else output.appendChild(line);
  output.scrollTop = output.scrollHeight;
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// ============================================================
// DASHBOARD
// ============================================================
async function refreshDashboard() {
  try {
    const stats = await getStats();
    if (!stats) return;

    document.getElementById('stat-total').textContent = stats.totalScans;
    document.getElementById('stat-avg-risk').textContent = stats.avgRisk;
    document.getElementById('stat-critical').textContent = stats.distribution.critical;
    document.getElementById('stat-high').textContent = stats.distribution.high;

    const total = stats.totalScans || 1;
    const d = stats.distribution;
    setDistBar('dist-critical', d.critical, total, 'dist-critical-n');
    setDistBar('dist-high', d.high, total, 'dist-high-n');
    setDistBar('dist-medium', d.medium, total, 'dist-medium-n');
    setDistBar('dist-low', d.low, total, 'dist-low-n');
    setDistBar('dist-clean', d.clean, total, 'dist-clean-n');
  } catch (e) { /* silent */ }
}

function setDistBar(barId, count, total, countId) {
  const pct = Math.round((count / total) * 100);
  document.getElementById(barId).style.width = `${pct}%`;
  document.getElementById(countId).textContent = count;
}

function updateDefcon(threatLevel) {
  const indicator = document.getElementById('defcon-indicator');
  const value = document.getElementById('defcon-value');
  indicator.className = 'defcon';

  if (threatLevel === 'CRITICAL') {
    indicator.classList.add('defcon-critical');
    value.textContent = 'CRITICAL';
  } else if (threatLevel === 'HIGH') {
    indicator.classList.add('defcon-high');
    value.textContent = 'ELEVATED';
  } else if (threatLevel === 'MEDIUM') {
    indicator.classList.add('defcon-medium');
    value.textContent = 'GUARDED';
  } else {
    indicator.classList.add('defcon-5');
    value.textContent = 'NOMINAL';
  }
}

// ============================================================
// HISTORY
// ============================================================
function initHistory() {
  document.getElementById('export-btn').onclick = exportHistory;
  const exportCsvBtn = document.getElementById('export-csv-history-btn');
  if (exportCsvBtn) exportCsvBtn.onclick = exportHistoryCsv;
}

async function refreshHistory() {
  try {
    const scans = await getRecentScans();
    const tbody = document.getElementById('history-tbody');
    const empty = document.getElementById('history-empty');

    if (scans.length === 0) {
      empty.style.display = 'block';
      return;
    }
    empty.style.display = 'none';

    tbody.innerHTML = scans.map(s => `
      <tr onclick="window.__viewScan('${s.id}')">
        <td style="font-family:var(--font-mono);font-size:0.7rem;">${s.domain}</td>
        <td style="font-family:var(--font-mono);font-weight:700;color:${getRiskColor(s.threatLevel)}">${s.overallRisk}</td>
        <td><span class="threat-badge threat-${s.threatLevel}">${s.threatLevel}</span></td>
        <td style="font-size:0.65rem;color:var(--text-dim)">${new Date(s.createdAt).toLocaleTimeString()}</td>
      </tr>
    `).join('');
  } catch (e) { /* silent */ }
}

// Global function for onclick in table rows
window.__viewScan = async (id) => {
  try {
    const result = await getScanResult(id);
    showResultPanel(result);
  } catch (e) {
    showToast('Could not load scan details', 'error');
  }
};

async function exportHistory() {
  try {
    const scans = await getRecentScans();
    const blob = new Blob([JSON.stringify(scans, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `venom-map-history-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('History exported as JSON', 'success');
  } catch (e) {
    showToast('Export failed', 'error');
  }
}

async function exportHistoryCsv() {
  try {
    const scans = await getRecentScans();
    exportHistoryAsCsv(scans);
    showToast('History exported as CSV', 'success');
  } catch (e) {
    showToast('CSV export failed', 'error');
  }
}

// ============================================================
// TOAST NOTIFICATIONS
// ============================================================
function showToast(message, type = 'info') {
  const container = document.getElementById('toast-container');
  const toast = document.createElement('div');
  toast.className = `toast ${type}`;
  toast.textContent = message;
  container.appendChild(toast);
  setTimeout(() => {
    toast.classList.add('toast-exit');
    setTimeout(() => toast.remove(), 300);
  }, 4000);
}

// ============================================================
// LOGS & DEBUG PANEL
// ============================================================
let logsAutoRefreshInterval = null;

function initLogs() {
  const refreshBtn = document.getElementById('refresh-logs');
  const clearBtn = document.getElementById('clear-logs');
  const levelFilter = document.getElementById('log-level-filter');

  refreshBtn.addEventListener('click', () => refreshLogs());
  clearBtn.addEventListener('click', () => {
    const entries = document.getElementById('logs-entries');
    entries.innerHTML = '';
    document.getElementById('logs-empty').style.display = 'block';
  });
  levelFilter.addEventListener('change', () => refreshLogs());
}

async function refreshLogs() {
  try {
    const level = document.getElementById('log-level-filter').value;
    const logs = await fetchLogs(level || null, 200);
    renderLogs(logs);
  } catch (e) {
    // Silent — server may not be running
  }
}

function renderLogs(logs) {
  const entries = document.getElementById('logs-entries');
  const empty = document.getElementById('logs-empty');

  if (!logs || logs.length === 0) {
    empty.style.display = 'block';
    entries.innerHTML = '';
    return;
  }
  empty.style.display = 'none';

  // Render logs in reverse (newest first)
  const reversed = [...logs].reverse();
  entries.innerHTML = reversed.map((log, idx) => {
    const time = new Date(log.timestamp).toTimeString().substring(0, 8);
    const scanTag = log.scanId ? log.scanId.substring(0, 8) : '—';
    const hasMeta = log.meta && Object.keys(log.meta).length > 0;

    let detailHtml = '';
    if (hasMeta) {
      const meta = log.meta;
      let metaLines = '';

      // Show structured meta data
      Object.entries(meta).forEach(([key, value]) => {
        if (key === 'stack') return; // Handle separately
        const valStr = typeof value === 'object' ? JSON.stringify(value) : String(value);
        metaLines += `<span class="meta-key">${escapeHtml(key)}:</span> <span class="meta-val">${escapeHtml(valStr)}</span>\n`;
      });

      // Stack trace (for errors)
      let stackHtml = '';
      if (meta.stack) {
        stackHtml = `<div class="stack-trace">${escapeHtml(meta.stack)}</div>`;
      }

      detailHtml = `<div class="log-detail" id="log-detail-${idx}">${metaLines}${stackHtml}</div>`;
    }

    return `
      <div class="log-entry log-entry-${log.level}" onclick="${hasMeta ? `document.getElementById('log-detail-${idx}').classList.toggle('open')` : ''}">
        <span class="log-time">${time}</span>
        <span class="log-level log-level-${log.level}">${log.level}</span>
        <span class="log-message">${escapeHtml(log.message)}</span>
        <span class="log-scan-id">${scanTag}</span>
      </div>
      ${detailHtml}
    `;
  }).join('');
}

// ============================================================
// LOAD INITIAL DATA
// ============================================================
async function loadInitialData() {
  try {
    await refreshDashboard();
    await refreshHistory();
    await refreshLogs();
    const globeData = await getGlobeData();
    globe.updateFromData(globeData);
  } catch (e) {
    // Server might not be running yet
    terminalLog('Waiting for backend connection...', 'warning');
  }
}

// ============================================================
// COMPARISON REPORT
// ============================================================
function initComparison() {
  const btn = document.getElementById('compare-btn');
  const input = document.getElementById('compare-domain');
  const closeBtn = document.getElementById('close-compare');

  btn.addEventListener('click', () => runComparison());
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') runComparison();
  });
  closeBtn.addEventListener('click', () => {
    document.getElementById('compare-content').innerHTML =
      '<div id="compare-empty" class="empty-state">Enter a domain to see before/after comparison.<br>Requires at least 2 scans of the same domain.</div>';
  });
}

async function runComparison() {
  const input = document.getElementById('compare-domain');
  const content = document.getElementById('compare-content');
  const domain = input.value.trim();

  if (!domain) {
    showToast('Enter a domain to compare', 'warning');
    return;
  }

  content.innerHTML = '<div class="empty-state">Loading comparison...</div>';

  try {
    const data = await fetchComparison(domain);
    if (!data || !data.comparison === null && data.totalScans < 2) {
      content.innerHTML = `<div class="empty-state">${data?.message || 'Need at least 2 scans of this domain to compare.'}</div>`;
      return;
    }

    if (data.totalScans < 2) {
      content.innerHTML = `<div class="empty-state">${data.message || 'Need at least 2 scans.'}</div>`;
      return;
    }

    renderComparison(data, content);
  } catch (e) {
    content.innerHTML = '<div class="empty-state">Failed to load comparison data.</div>';
    showToast('Comparison failed', 'error');
  }
}

function renderComparison(data, container) {
  const delta = data.riskDelta;
  const deltaClass = delta > 0 ? 'delta-up' : delta < 0 ? 'delta-down' : 'delta-same';
  const deltaIcon = delta > 0 ? '⬆' : delta < 0 ? '⬇' : '=';
  const firstLevel = data.first.threatLevel;
  const lastLevel = data.last.threatLevel;

  let scannersHtml = '';
  if (data.scannerComparison && data.scannerComparison.length > 0) {
    scannersHtml = data.scannerComparison.map(sc => {
      const sDelta = sc.delta;
      const sDeltaClass = sDelta > 0 ? 'delta-up' : sDelta < 0 ? 'delta-down' : 'delta-same';
      return `
        <div class="compare-scanner-row">
          <div class="compare-scanner-code">${sc.code}</div>
          <div class="compare-scanner-name">${sc.name}</div>
          <div class="compare-scanner-scores">
            <span class="score-before">${sc.scoreBefore}</span>
            <span class="score-arrow">→</span>
            <span class="score-after">${sc.scoreAfter}</span>
            <span class="score-delta ${sDeltaClass}">${sDelta > 0 ? '+' : ''}${sDelta}</span>
          </div>
        </div>
      `;
    }).join('');
  }

  let timelineHtml = '';
  if (data.timeline && data.timeline.length > 0) {
    timelineHtml = data.timeline.map(t => {
      const time = new Date(t.date).toLocaleString();
      return `
        <div class="timeline-dot">
          <span class="timeline-risk" style="color:${getRiskColor(t.level)}">${t.risk}</span>
          <span class="timeline-badge threat-${t.level}">${t.level}</span>
          <span class="timeline-time">${time}</span>
        </div>
      `;
    }).join('');
  }

  container.innerHTML = `
    <div class="compare-summary">
      <div class="compare-domain-title">${data.domain}</div>
      <div class="compare-overview">
        <div class="compare-box compare-before">
          <div class="compare-box-label">BEFORE</div>
          <div class="compare-box-risk" style="color:${getRiskColor(firstLevel)}">${data.first.overallRisk}</div>
          <div class="compare-box-level threat-badge threat-${firstLevel}">${firstLevel}</div>
          <div class="compare-box-date">${new Date(data.first.date).toLocaleDateString()}</div>
        </div>
        <div class="compare-delta ${deltaClass}">
          <div class="compare-delta-icon">${deltaIcon}</div>
          <div class="compare-delta-value">${delta > 0 ? '+' : ''}${delta}</div>
          <div class="compare-delta-label">RISK DELTA</div>
        </div>
        <div class="compare-box compare-after">
          <div class="compare-box-label">AFTER</div>
          <div class="compare-box-risk" style="color:${getRiskColor(lastLevel)}">${data.last.overallRisk}</div>
          <div class="compare-box-level threat-badge threat-${lastLevel}">${lastLevel}</div>
          <div class="compare-box-date">${new Date(data.last.date).toLocaleDateString()}</div>
        </div>
      </div>
    </div>
    <div class="compare-scanners">
      <h3>SCANNER BREAKDOWN</h3>
      ${scannersHtml || '<div class="empty-state">No scanner details available</div>'}
    </div>
    <div class="compare-timeline">
      <h3>SCAN TIMELINE (${data.totalScans} scans)</h3>
      <div class="timeline-track">
        ${timelineHtml}
      </div>
    </div>
  `;
}
